import React, { useEffect, useRef, useState } from 'react'
import { Button, Input, InputFile } from '../../lib/components'
import { displayDollar } from '../../lib/utils/helpers'
import { FileDataProps, FileUpload } from '../../lib/types'
import { WalletTopup } from '../../services/wallet-service'
import { InvestV2WireTransferCard } from '../invest-v2/invest-v2-wire-transfer-card'
import {
  SWIFT_COPY_MISSING_REASON,
  SWIFT_COPY_ATTACHED_REASON,
  TOPUP_AWAITING_CONFIRMATION_REASON,
  topupHasProof,
} from './wallet-ledger'
import '../invest-v2/invest-v2-bank-cards.scss'

type Props = {
  openTopups: WalletTopup[]
  canIntroduceCents: number
  investmentLimitCents?: number
  initialAmountDollars?: string
  isBusy: boolean
  onCreateTopup: (amountCents: number) => Promise<number | void>
  onUpdateAmount: (topupId: number, amountCents: number) => Promise<void>
  onSubmitProof: (topupId: number, paymentCopy: string) => Promise<void>
  onCancelTopup?: (topupId: number) => Promise<void>
  handleFileUpload: (file: File) => Promise<{ data: FileUpload; status: number } | undefined>
  hideTitle?: boolean
}

const money = (cents: number) => displayDollar(String(cents), 'USD', 2, true, true)

const topupNeedsProof = (topup: WalletTopup) => !topupHasProof(topup)

const preventNumberWheel = (event: React.WheelEvent<HTMLDivElement>) => {
  const target = event.target as HTMLElement | null
  if (target?.tagName === 'INPUT') {
    ;(target as HTMLInputElement).blur()
  }
}

const WalletAmountInput = ({
  name,
  value,
  onChange,
  onBlur,
  disabled,
}: {
  name: string
  value: string
  onChange: (next: string) => void
  onBlur?: () => void
  disabled?: boolean
}) => (
  <div className='wallet-page__amount-field'>
    <label className='input-label' htmlFor={name}>
      Amount
    </label>
    <div className='wallet-page__amount-input-wrap' onWheel={preventNumberWheel}>
      <span className='wallet-page__amount-prefix' aria-hidden>
        $
      </span>
      <Input
        type='number'
        name={name}
        placeholder='0.00'
        value={value}
        disabled={disabled}
        handleChange={(e) => onChange(e.target.value)}
        {...(onBlur
          ? {
              onBlur: () => {
                onBlur()
              },
            }
          : {})}
      />
    </div>
  </div>
)

export const WalletTopupForm = ({
  openTopups,
  canIntroduceCents,
  investmentLimitCents = 0,
  initialAmountDollars = '',
  isBusy,
  onCreateTopup,
  onUpdateAmount,
  onSubmitProof,
  onCancelTopup,
  handleFileUpload,
  hideTitle = false,
}: Props) => {
  const [amount, setAmount] = useState(initialAmountDollars || '')
  const [editAmounts, setEditAmounts] = useState<Record<number, string>>({})
  const [wireExpanded, setWireExpanded] = useState(true)
  const [proofByTopup, setProofByTopup] = useState<
    Record<number, { paymentCopy: string; fileInfo: { name: string; size: string } }>
  >({})
  const [confirmCancelTopupId, setConfirmCancelTopupId] = useState<number | null>(null)
  const [scrollToProofTopupId, setScrollToProofTopupId] = useState<number | null>(null)
  const proofSectionRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const createInFlight = useRef(false)
  const didAutoCreateFromPrefill = useRef(false)

  const hasOpenTopup = openTopups.length > 0
  const openTopupNeedsProof = openTopups.some(topupNeedsProof)
  const investmentLimitUnset = !(Number(investmentLimitCents) > 0)
  const canCreate = !hasOpenTopup && !investmentLimitUnset && canIntroduceCents > 0
  const createCents = Math.round(Number(amount) * 100)

  useEffect(() => {
    if (initialAmountDollars) {
      setAmount(initialAmountDollars)
    }
  }, [initialAmountDollars])

  useEffect(() => {
    setEditAmounts((prev) => {
      const next = { ...prev }
      openTopups.forEach((topup) => {
        if (next[topup.id] === undefined) {
          next[topup.id] = String(topup.amount_cents / 100)
        }
      })
      return next
    })
  }, [openTopups])

  useEffect(() => {
    if (scrollToProofTopupId === null) return
    const proofSection = proofSectionRefs.current[scrollToProofTopupId]
    if (!proofSection) return
    proofSection.scrollIntoView({ behavior: 'smooth', block: 'start' })
    setScrollToProofTopupId(null)
  }, [openTopups, scrollToProofTopupId])

  const tryCreateFromAmount = async () => {
    if (createInFlight.current || isBusy || hasOpenTopup) return
    if (!(createCents > 0) || createCents > canIntroduceCents) return
    createInFlight.current = true
    try {
      const createdTopupId = await onCreateTopup(createCents)
      setAmount('')
      if (createdTopupId !== undefined) {
        setScrollToProofTopupId(createdTopupId)
        setWireExpanded(true)
      }
    } finally {
      createInFlight.current = false
    }
  }

  useEffect(() => {
    if (didAutoCreateFromPrefill.current) return
    if (!initialAmountDollars || isBusy || !canCreate || hasOpenTopup) return
    const cents = Math.round(Number(initialAmountDollars) * 100)
    if (!(cents > 0) || cents > canIntroduceCents) return
    didAutoCreateFromPrefill.current = true
    if (createInFlight.current) return
    createInFlight.current = true
    void (async () => {
      try {
        const createdTopupId = await onCreateTopup(cents)
        setAmount('')
        if (createdTopupId !== undefined) {
          setScrollToProofTopupId(createdTopupId)
          setWireExpanded(true)
        }
      } finally {
        createInFlight.current = false
      }
    })()
  }, [initialAmountDollars, canIntroduceCents, canCreate, hasOpenTopup, isBusy, onCreateTopup])

  useEffect(() => {
    if (!hasOpenTopup || !initialAmountDollars) return
    const firstOpen = openTopups[0]
    if (!firstOpen) return
    setScrollToProofTopupId(firstOpen.id)
    setWireExpanded(true)
  }, [hasOpenTopup, initialAmountDollars, openTopups])

  const handleEditAmountBlur = (topup: WalletTopup, editValue: string) => {
    if (isBusy || !topupNeedsProof(topup)) return
    const editCents = Math.round(Number(editValue) * 100)
    const currentCents = Math.round(Number(topup.amount_cents))
    const maxEditCents = Number(canIntroduceCents)
    if (!(editCents > 0) || editCents === currentCents) return
    if (editCents > maxEditCents) return
    void onUpdateAmount(topup.id, editCents)
  }

  const handleFileChange = (topupId: number, fileData: FileDataProps | undefined) => {
    if (!fileData) return
    void (async () => {
      const file = fileData as File
      const uploaded = await handleFileUpload(file)
      if (uploaded?.status === 200) {
        setProofByTopup((prev) => ({
          ...prev,
          [topupId]: {
            paymentCopy: uploaded.data.key,
            fileInfo: { name: file.name, size: `${Math.round(file.size / 1024)} KB` },
          },
        }))
      }
    })()
  }

  return (
    <section
      className={`wallet-page__form${
        hideTitle ? ' wallet-page__form--modal' : ' dashboard-v2__profile-card'
      }`}
      data-testid='wallet-topup-form'
    >
      {hideTitle ? null : <h2>Add Funds</h2>}
      <p>Funds are added to your wallet once we confirm receipt of the transfer.</p>

      <div className='wallet-page__transfer-checklist'>
        <p className='wallet-page__transfer-checklist-title'>Please note:</p>
        <ul>
          <li>Use the bank account registered during your KYC.</li>
          <li>Bank or intermediary charges may reduce the amount received.</li>
          <li>SWIFT copy to be uploaded after the transfer is completed.</li>
        </ul>
      </div>

      {hasOpenTopup || canCreate ? (
        <p className='wallet-page__field-hint'>
          {hasOpenTopup
            ? 'Complete your existing top-up before starting a new one.'
            : 'Enter the intended amount and select Continue to create a top-up.'}
        </p>
      ) : null}

      {hasOpenTopup ? (
        <p className='wallet-page__notice' data-testid='wallet-topup-incomplete-prompt'>
          {openTopupNeedsProof
            ? 'You have a pending wallet top-up. Please upload the SWIFT copy to complete it, or cancel the existing top-up request.'
            : 'You have a pending wallet top-up awaiting confirmation.'}
        </p>
      ) : investmentLimitUnset ? (
        <p className='wallet-page__notice' data-testid='wallet-topup-limit-unset'>
          Your investment limit has not yet been set; capacity is currently unavailable.
        </p>
      ) : canCreate ? (
        <>
          <WalletAmountInput name='topup' value={amount} onChange={setAmount} disabled={isBusy} />
          {createCents > canIntroduceCents ? (
            <p
              className='wallet-page__field-hint wallet-page__field-hint--error'
              data-testid='wallet-topup-capacity-exceeded'
            >
              The requested amount exceeds the remaining available investment limit (
              {money(canIntroduceCents)}).
            </p>
          ) : null}
          <Button
            text='Continue'
            variant='primary-btn'
            isDisabled={isBusy || !(createCents > 0) || createCents > canIntroduceCents}
            handleClick={() => void tryCreateFromAmount()}
            testId='wallet-topup-continue'
          />
        </>
      ) : (
        <p
          className='wallet-page__field-hint wallet-page__field-hint--error'
          data-testid='wallet-topup-no-capacity'
        >
          The requested amount exceeds the remaining available investment limit.
        </p>
      )}

      {openTopups.length > 0 ? (
        <div className='wallet-page__open-topups'>
          <h3>Open top-ups</h3>
          {openTopups.map((topup) => {
            const needsProof = topupNeedsProof(topup)
            const hasProof = topupHasProof(topup)
            const currentCents = Math.round(Number(topup.amount_cents))
            const editValue = editAmounts[topup.id] ?? String(currentCents / 100)
            const editCents = Math.round(Number(editValue) * 100)
            // Pending top-ups no longer reduce canIntroduce (limit - total invested), so the
            // edited amount itself must fit within it.
            const maxEditCents = Number(canIntroduceCents)
            const editExceedsLimit =
              Number.isFinite(editCents) &&
              Number.isFinite(maxEditCents) &&
              editCents > maxEditCents
            const proof = proofByTopup[topup.id] || {
              paymentCopy: '',
              fileInfo: { name: '', size: '' },
            }
            const localProofAttached =
              typeof proof.paymentCopy === 'string' && proof.paymentCopy.trim() !== ''
            const proofStatusLabel = hasProof
              ? TOPUP_AWAITING_CONFIRMATION_REASON
              : localProofAttached
              ? SWIFT_COPY_ATTACHED_REASON
              : SWIFT_COPY_MISSING_REASON
            const confirmingCancel = confirmCancelTopupId === topup.id

            return (
              <div
                key={topup.id}
                className='wallet-page__open-topup'
                data-testid={`open-topup-${topup.id}`}
              >
                <div className='wallet-page__open-topup-head'>
                  <div className='wallet-page__open-topup-summary'>
                    <strong>{topup.order_id}</strong>
                    <span>
                      {money(currentCents)} · {proofStatusLabel}
                    </span>
                  </div>
                </div>

                {needsProof ? (
                  <>
                    <WalletAmountInput
                      name={`topup-edit-${topup.id}`}
                      value={editValue}
                      onChange={(next) => setEditAmounts((prev) => ({ ...prev, [topup.id]: next }))}
                      onBlur={() => handleEditAmountBlur(topup, editValue)}
                      disabled={isBusy}
                    />
                    {editExceedsLimit ? (
                      <p
                        className='wallet-page__field-hint wallet-page__field-hint--error'
                        data-testid={`wallet-topup-edit-capacity-exceeded-${topup.id}`}
                      >
                        The requested amount exceeds the remaining available investment limit (
                        {money(maxEditCents)}).
                      </p>
                    ) : null}

                    <div
                      className='wallet-page__proof'
                      ref={(el) => {
                        proofSectionRefs.current[topup.id] = el
                      }}
                      data-testid={`wallet-topup-proof-${topup.id}`}
                    >
                      <h3>Proof of transfer</h3>
                      <p>Upload your SWIFT / payment slip to submit this top-up.</p>
                      <InputFile
                        setRemoveFile={() =>
                          setProofByTopup((prev) => ({
                            ...prev,
                            [topup.id]: {
                              ...proof,
                              paymentCopy: '',
                              fileInfo: { name: '', size: '' },
                            },
                          }))
                        }
                        type='doc'
                        label='Upload SWIFT copy'
                        testId={`wallet-topup-file-input-${topup.id}`}
                        name={`swiftCopyBank-${topup.id}`}
                        handleFiles={(fileData) => handleFileChange(topup.id, fileData)}
                        fileInfo={proof.fileInfo}
                      />
                      <div className='wallet-page__open-topup-actions'>
                        <Button
                          text='I have completed the transfer'
                          variant='primary-btn'
                          isDisabled={
                            isBusy || !proof.paymentCopy || editExceedsLimit || !(editCents > 0)
                          }
                          handleClick={() => void onSubmitProof(topup.id, proof.paymentCopy)}
                        />
                        {onCancelTopup ? (
                          confirmingCancel ? (
                            <div
                              className='wallet-page__cancel-confirm'
                              data-testid={`wallet-topup-form-cancel-confirm-${topup.id}`}
                            >
                              <p>Cancel this top-up?</p>
                              <div className='wallet-page__cancel-confirm-actions'>
                                <button
                                  type='button'
                                  className='secondary small'
                                  disabled={isBusy}
                                  data-testid={`wallet-topup-form-confirm-cancel-${topup.id}`}
                                  onClick={() => {
                                    setConfirmCancelTopupId(null)
                                    void onCancelTopup(topup.id)
                                  }}
                                >
                                  Yes, cancel
                                </button>
                                <button
                                  type='button'
                                  className='secondary small'
                                  disabled={isBusy}
                                  data-testid={`wallet-topup-form-keep-${topup.id}`}
                                  onClick={() => setConfirmCancelTopupId(null)}
                                >
                                  Keep
                                </button>
                              </div>
                            </div>
                          ) : (
                            <button
                              type='button'
                              className='wallet-page__cancel-link'
                              disabled={isBusy}
                              data-testid={`wallet-topup-form-cancel-${topup.id}`}
                              onClick={() => setConfirmCancelTopupId(topup.id)}
                            >
                              Cancel
                            </button>
                          )
                        ) : null}
                      </div>
                    </div>
                  </>
                ) : onCancelTopup ? (
                  <div className='wallet-page__open-topup-actions'>
                    {confirmingCancel ? (
                      <div
                        className='wallet-page__cancel-confirm'
                        data-testid={`wallet-topup-form-cancel-confirm-${topup.id}`}
                      >
                        <p>Cancel this top-up?</p>
                        <div className='wallet-page__cancel-confirm-actions'>
                          <button
                            type='button'
                            className='secondary small'
                            disabled={isBusy}
                            data-testid={`wallet-topup-form-confirm-cancel-${topup.id}`}
                            onClick={() => {
                              setConfirmCancelTopupId(null)
                              void onCancelTopup(topup.id)
                            }}
                          >
                            Yes, cancel
                          </button>
                          <button
                            type='button'
                            className='secondary small'
                            disabled={isBusy}
                            data-testid={`wallet-topup-form-keep-${topup.id}`}
                            onClick={() => setConfirmCancelTopupId(null)}
                          >
                            Keep
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type='button'
                        className='wallet-page__cancel-link'
                        disabled={isBusy}
                        data-testid={`wallet-topup-form-cancel-${topup.id}`}
                        onClick={() => setConfirmCancelTopupId(topup.id)}
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                ) : null}
              </div>
            )
          })}
        </div>
      ) : null}

      <div className='wallet-page__collapse'>
        <button
          type='button'
          className='wallet-page__collapse-toggle'
          aria-expanded={wireExpanded}
          onClick={() => setWireExpanded((prev) => !prev)}
        >
          <span>Wire transfer details</span>
          <span>{wireExpanded ? 'Hide' : 'Show'}</span>
        </button>
        {wireExpanded ? <InvestV2WireTransferCard showAssistanceNote /> : null}
      </div>
    </section>
  )
}
